import { ShimmerEffect } from "@/components/shimmer";
import { assetUrl, cn } from "utils";
import { useSidebar } from "ui/components/sidebar";

/**
 * A collapsible project header component that adapts to sidebar state
 * Shows full header when expanded, only logo when collapsed
 * @param {Object} props - Component props
 * @param {Object} props.casestudy.project - The project data object
 * @param {string} props.casestudy.project.name - Project name
 * @param {string} props.casestudy.project.logo - Project logo asset ID
 * @param {string} props.casestudy.project.status - Project status
 * @param {boolean} props.isLoading - Whether the project data is loading
 * @param {string} props.className - Additional CSS classes
 */
export function CollapsibleProjectHeader({ casestudy, isLoading = false, className = "" }) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  if (isLoading) {
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        <div className={cn("aspect-square", isCollapsed ? "size-8" : "size-44")}>
          <ShimmerEffect className="w-full h-full rounded" />
        </div>
        {!isCollapsed && (
          <div>
            <ShimmerEffect className="w-40 h-5" />
            <ShimmerEffect className="w-20 h-4 mt-1" />
          </div>
        )}
      </div>
    );
  }

  if (!casestudy) {
    return null;
  }

  const logoContent = casestudy.project.logo ? (
    <img
      src={assetUrl(casestudy.project.logo)}
      alt={`${casestudy.project.name} logo`}
      className="w-full h-full object-contain"
    />
  ) : (
    <div className="w-full h-full bg-gray-200 flex items-center justify-center rounded">
      <span className={cn(
        "text-gray-500",
        isCollapsed ? "text-xs" : "text-6xl"
      )}>
          {casestudy.project.name[0]}
      </span>
    </div>
  );

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {/* Logo - always visible but size changes */}
      <div className={cn(
        "aspect-square transition-all duration-200",
        isCollapsed ? "size-6" : "size-44"
      )}>
        {logoContent}
      </div>
      
      {/* Project details - hidden when collapsed */}
      {!isCollapsed && (
        <div className="transition-all duration-200">
          <h2 className="font-semibold text-lg">{casestudy.name}</h2>
          <h2 className="text-muted-foreground text-sm">{casestudy.project.name}</h2>
        </div>
      )}
    </div>
  );
}
